// Implements: F-UIX-015
/**
 * Every string the interface says, in neutral Spanish.
 *
 * The keys are English and flat, `<area>.<surface>.<element>` (AD-05); the
 * VALUE is where the Spanish lives. That split is the whole point: a component
 * names a key, so a reader of the code sees intent, and this file is the one
 * place a wording change happens.
 *
 * Flat keys with dots rather than a nested object: `keyof typeof es` then
 * yields the literal key strings straight away, which is what makes a
 * misspelled key a type error instead of a runtime `undefined`.
 *
 * `{seconds}` in `auth.rateLimit.countdown` is the only placeholder of this
 * change, and `t()` refuses to build that string without it.
 */
import { esAccount } from "./es.account";
import { esDesign } from "./es.design";
import { esDesignDelete, esDesignReadOnly } from "./es.p5";
import { esOrganization } from "./es.organization";
import { esMockupsAccess } from "./es.p4";
import { esDiagrams } from "./es.p6";
import { esCosts } from "./es.p7";
import { esGalleryLinks } from "./es.p8";
import { esReports } from "./es.p9";

export const es = {
  "common.action.submit": "Enviar",
  "common.action.cancel": "Cancelar",
  "common.action.retry": "Reintentar",
  "common.action.close": "Cerrar",
  "common.moreInfo": "Más información: {subject}",
  "common.action.confirm": "Confirmar",
  "common.action.dismiss": "Descartar",
  "common.yes": "Sí",
  "common.no": "No",

  "common.state.loading": "Cargando…",
  "common.state.empty": "No hay nada que mostrar todavía.",
  "common.state.error": "No se pudo completar la operación.",
  "common.state.unknownError": "Ha ocurrido un error inesperado. Vuelve a intentarlo.",
  "common.state.noResults": "Sin resultados.",
  "common.state.rateLimitWait": "Podrás volver a enviar en {seconds} s.",
  "common.state.rateLimitWaitStatus": "Se han hecho demasiadas solicitudes seguidas. Podrás volver a enviar en unos segundos.",
  /** The em dash a field the backend left empty is rendered as. */
  "common.value.unknown": "—",

  "common.theme.label": "Tema",
  "common.theme.system": "Del sistema",
  "common.theme.light": "Claro",
  "common.theme.dark": "Oscuro",

  "auth.login.title": "Iniciar sesión",
  "auth.login.subtitle": "Accede con la cuenta de tu organización.",
  "auth.login.submit": "Entrar",
  "auth.login.emailLabel": "Correo electrónico",
  "auth.login.passwordLabel": "Contraseña",

  "auth.login.error.invalidCredentials": "Las credenciales no son válidas.",
  // Owner 2026-10-19: heard only once every factor of the account was proven (403 FORBIDDEN).
  "auth.login.error.organizationsSuspended":
    "Tus organizaciones están suspendidas, así que no puedes entrar. Podrás volver a entrar cuando se reactive alguna.",
  "auth.login.error.unexpected": "No se pudo iniciar sesión. Vuelve a intentarlo.",
  "auth.login.error.validation": "Revisa los datos del formulario.",

  "auth.login.help": "Si tu cuenta tiene segundo factor, te lo pediremos en el siguiente paso.",
  // P2: the public password recovery pages (v3 `vForgot`, `vReset`).
  "auth.login.forgot": "¿Olvidaste tu contraseña?",
  "auth.recovery.title": "Recupera tu contraseña",
  "auth.recovery.subtitle": "Escribe el correo con el que entras y te enviaremos un enlace para crear una nueva.",
  "auth.recovery.emailPlaceholder": "nombre@empresa.pe",
  "auth.recovery.submit": "Enviar el enlace",
  "auth.recovery.back": "Volver a iniciar sesión",
  "auth.recovery.invalidEmail": "Escribe un correo válido, por ejemplo nombre@empresa.pe.",
  "auth.recovery.failed": "No se pudo enviar el correo ahora. Vuelve a intentarlo en unos minutos.",
  "auth.recovery.sent.title": "Revisa tu correo",
  "auth.recovery.sent.body":
    "Si {email} tiene una cuenta, te enviamos un enlace para crear una contraseña nueva. Si no lo ves, mira en el correo no deseado.",
  "auth.recovery.again": "Usar otro correo",
  "auth.reset.title": "Crea una contraseña nueva",
  "auth.reset.new": "Contraseña nueva",
  "auth.reset.confirm": "Repite la contraseña",
  "auth.reset.submit": "Guardar la contraseña",
  "auth.reset.missing": "Escribe la contraseña nueva.",
  "auth.reset.mismatch": "Las contraseñas no coinciden.",
  "auth.reset.failed": "No se pudo guardar la contraseña. Vuelve a intentarlo.",
  "auth.reset.gone.title": "Este enlace ya no sirve",
  "auth.reset.gone.body": "Caducó o ya se usó. Pide otro y úsalo en cuanto te llegue.",
  "auth.reset.gone.action": "Pedir otro enlace",
  "auth.reset.done.title": "Contraseña actualizada",
  "auth.reset.done.body": "Ya puedes entrar con tu contraseña nueva.",
  "auth.reset.done.action": "Iniciar sesión",
  "auth.secondFactor.title": "Código del segundo factor",
  "auth.secondFactor.subtitle": "Abre tu aplicación de autenticación para ver el código.",
  "auth.secondFactor.submit": "Verificar y entrar",
  "auth.secondFactor.back": "Volver",
  "auth.secondFactor.label": "Código del segundo factor",
  "auth.secondFactor.hint": "Introduce el código de seis dígitos de tu aplicación de autenticación.",
  "auth.secondFactor.required": "Esta cuenta pide un segundo factor.",
  "auth.secondFactor.invalid": "El código del segundo factor no es válido.",

  "auth.rateLimit.title": "Demasiados intentos",
  "auth.rateLimit.body": "Por seguridad, el acceso queda en espera durante unos segundos.",
  "auth.rateLimit.countdown": "Puedes volver a intentarlo en {seconds} segundos.",

  "auth.logout.action": "Cerrar sesión",
  "auth.logout.confirmTitle": "¿Cerrar la sesión?",
  "auth.logout.confirmBody": "Se cerrará la sesión en este dispositivo.",
  /**
   * Said when the browser was emptied but the server never confirmed. It names
   * the doubt instead of hiding it, because somebody leaving a shared machine
   * needs to know the session may outlive the screen they are looking at.
   */
  "auth.logout.failed":
    "No hemos podido confirmar el cierre de sesión con el servidor. Es posible que siga abierta: vuelve a iniciar sesión para cerrarla o revócala desde otro dispositivo.",

  "auth.sessions.title": "Sesiones activas",
  "auth.sessions.empty": "No hay ninguna otra sesión abierta.",
  "auth.sessions.emptyBody": "Solo está abierta la sesión de este dispositivo.",
  "auth.sessions.current": "Sesión actual",
  "auth.sessions.device": "Dispositivo",
  "auth.sessions.lastSeen": "Última actividad",
  "auth.sessions.ip": "Dirección IP",
  "auth.sessions.actions": "Acciones",
  "auth.sessions.subtitle": "Dispositivos con tu cuenta abierta.",
  "auth.sessions.revoke": "Revocar",
  "auth.sessions.revokeLabel": "Revocar la sesión de {device}",
  "auth.sessions.revokeCurrentLabel": "Revocar la sesión actual ({device})",
  "auth.sessions.revokeConfirmTitle": "¿Revocar la sesión?",
  "auth.sessions.revokeConfirmBody": "El dispositivo tendrá que iniciar sesión de nuevo.",
  "auth.sessions.revokeCurrentConfirmTitle": "¿Revocar la sesión actual?",
  "auth.sessions.revokeCurrentConfirmBody":
    "Es la sesión de este dispositivo: al revocarla se cerrará tu sesión aquí y tendrás que volver a entrar.",
  "auth.sessions.revoked": "La sesión se ha revocado.",

  "app.nav.lienzo": "Lienzo",
  "app.nav.gallery": "Galería",
  "app.nav.sessions": "Sesiones",
  "app.nav.design": "Diseño",
  "app.nav.sharing": "Enlaces compartidos",
  "app.nav.backoffice": "Backoffice",
  "app.nav.platform": "Plataforma",
  "app.nav.whatsapp": "WhatsApp",
  "app.nav.signOut": "Cerrar sesión",
  "app.nav.groupWork": "Trabajo",
  "app.nav.groupOrganization": "Organización",
  "app.nav.consumption": "Consumo",
  "app.nav.distribution": "Distribución",
  "app.nav.audit": "Auditoría",
  "app.nav.platformTotals": "Totales",
  "app.nav.platformCeilings": "Topes",
  "app.nav.platformModels": "Modelos de lenguaje",
  "app.nav.platformStatus": "Estado del sistema",
  "app.nav.platformBadge": "solo admin",
  "app.nav.people": "Personas",
  "app.nav.teams": "Equipos",
  "app.nav.invitations": "Invitaciones",
  "app.nav.platformOrganizations": "Organizaciones",
  "app.account.profile": "Perfil y seguridad",
  "app.account.sessions": "Sesiones activas",
  "app.account.accessLog": "Registro de accesos",
  "app.account.data": "Mis datos",
  "app.shell.brand": "Valtx Mockups",
  "app.shell.sidebar": "Navegación principal",
  "app.shell.sections": "Secciones",
  "app.shell.toggleSidebar": "Mostrar u ocultar la barra lateral",
  "app.shell.openNavigation": "Abrir la navegación",
  "app.shell.breadcrumb": "Ruta de navegación",
  "app.shell.account": "Cuenta",
  "app.org.switch": "{name}, {kind}: cambiar de espacio de trabajo",
  "app.org.label": "Espacios de trabajo",
  "app.org.kind": "Organización",
  "app.org.personal": "Personal",
  "app.org.personalHint": "Solo tú",
  "app.user.menu": "{name}, {email}: menú de usuario",
  "app.theme.cycle": "Tema: {theme}. Cambiar tema",
  "app.theme.current": "Tema: {theme}",
  "app.palette.label": "Paleta de comandos",
  "app.palette.placeholder": "Buscar o ir a…",
  "app.palette.results": "Resultados",
  "app.palette.empty": "Sin resultados para «{query}».",
  "app.palette.move": "moverse",
  "app.palette.open": "abrir",
  "app.palette.close": "cerrar",
  "app.palette.groupMockups": "Mockups",
  "app.palette.groupGo": "Ir a",
  "app.palette.groupPreferences": "Preferencias",
  "app.palette.groupAccount": "Cuenta",
  "app.palette.groupActions": "Acciones",
  "app.palette.newMockup": "Nuevo mockup en el Lienzo",
  "app.palette.toggleTheme": "Cambiar tema",
  "app.palette.invitePeople": "Invitar personas",
  "app.search.button": "Buscar o ir a…",
  "app.search.label": "Buscar o ir a… Ctrl K",
  "app.shortcuts.title": "Atajos de teclado",
  "app.shortcuts.palette": "Abrir la paleta de comandos",
  "app.shortcuts.sidebar": "Mostrar u ocultar la barra lateral",
  "app.shortcuts.help": "Ver los atajos",
  "app.shortcuts.send": "Crear mockup o enviar mensaje",
  "app.shortcuts.close": "Cerrar el diálogo o el menú",
  "app.shortcuts.focus": "Contraer o expandir la conversación en el editor",
  "app.kbd.ctrl": "Ctrl",
  "app.kbd.ctrlK": "Ctrl K",
  "app.kbd.ctrlEnter": "Ctrl Intro",
  "app.kbd.enter": "Intro",
  "app.kbd.esc": "Esc",
  "app.kbd.up": "↑",
  "app.kbd.down": "↓",
  "app.kbd.bracket": "[",
  "app.kbd.question": "?",
  "app.kbd.shiftBackslash": "Mayús \\",

  "app.notFound.title": "Página no encontrada",
  "app.notFound.body": "La dirección que has abierto no corresponde a ninguna pantalla.",
  "app.notFound.back": "Volver al inicio",
  "app.noAccess.title": "No tienes acceso a esta sección",
  "app.noAccess.body": "Esta sección es solo para administradores. Si la necesitas, pídele acceso a quien administra tu organización.",
  "app.noAccess.back": "Ir a la galería",

  "gallery.title": "Galería",
  "gallery.subtitle": "Encuentra, organiza y comparte tus mockups.",
  "gallery.tabs.label": "Filtro de la galería",
  "gallery.tab.all": "Todos",
  "gallery.tab.favorites": "Favoritos",
  "gallery.tab.trash": "Papelera",
  "gallery.search.label": "Buscar mockups",
  "gallery.search.placeholder": "Buscar por nombre o etiqueta",
  "gallery.scope.button": "Ámbito: {name}",
  "gallery.scope.menu": "Filtrar por ámbito",
  "gallery.scope.all": "Todos",
  "gallery.tag.button": "Etiqueta: {name}",
  "gallery.tag.menu": "Filtrar por etiqueta",
  "gallery.tag.all": "Todas",
  "gallery.view.label": "Modo de vista",
  "gallery.view.grid": "Cuadrícula",
  "gallery.view.list": "Lista",
  "gallery.col.name": "Nombre",
  "gallery.col.scope": "Ámbito",
  "gallery.col.updated": "Actualizado",
  "gallery.col.actions": "Acciones",
  "gallery.empty.title": "Todavía no hay mockups: crea el primero en el Lienzo.",
  "gallery.empty.body": "Cuando crees tu primer mockup aparecerá aquí.",
  "gallery.empty.action": "Ir al Lienzo",
  "gallery.empty.trash": "Ver papelera",
  "gallery.noResults.title": "Sin resultados",
  "gallery.noResults.body": "Ningún mockup coincide con lo que buscas. Prueba con otra palabra o quita los filtros.",
  "gallery.noResults.clear": "Quitar filtros",
  "gallery.favoritesEmpty.title": "Todavía no tienes favoritos",
  "gallery.favoritesEmpty.body": "Marca un mockup con la estrella y lo encontrarás aquí.",
  "gallery.card.menu": "Más acciones: {name}",
  "gallery.card.open": "Abrir en el Lienzo",
  "gallery.card.rename": "Renombrar",
  "gallery.card.move": "Mover a otro ámbito…",
  "gallery.card.share": "Compartir",
  "gallery.card.trash": "Mover a la papelera",
  "gallery.card.trashed": "«{name}» se movió a la papelera.",
  "gallery.card.trashFailed": "No se pudo mover el mockup a la papelera.",
  "gallery.rename.title": "Renombrar mockup",
  "gallery.rename.label": "Nombre del mockup",
  "gallery.rename.save": "Guardar nombre",
  "gallery.rename.failed": "No se pudo cambiar el nombre.",
  "gallery.move.title": "Mover a otro ámbito",
  "gallery.loadMore": "Cargar más",
  "gallery.card.updated": "Actualizado",
  "gallery.card.thumbnail": "Miniatura de {title}",
  "gallery.favorite.add": "Marcar como favorito",
  "gallery.favorite.remove": "Quitar de favoritos",
  "gallery.favorite.failed": "No se pudo actualizar el favorito.",

  "gallery.trash.title": "Papelera",
  "gallery.trash.subtitle": "Un mockup de la papelera se puede restaurar hasta que se borra definitivamente.",
  "gallery.trash.empty": "La papelera está vacía.",
  "gallery.trash.emptyBody": "Los mockups que envíes aquí se podrán restaurar.",
  "gallery.table.label": "Mockups de la galería",
  "gallery.trash.table.label": "Mockups en la papelera",
  "gallery.trash.col.name": "Nombre",
  "gallery.trash.col.trashed": "Enviado a la papelera",
  "gallery.trash.col.purge": "Se elimina el",
  "gallery.trash.purgeAfter": "Se elimina el",
  "gallery.trash.restore": "Restaurar",
  "gallery.trash.restored": "El mockup se ha restaurado.",
  "gallery.trash.deleteForever": "Borrar definitivamente",
  "gallery.trash.deleted": "El mockup se ha borrado definitivamente.",
  "gallery.trash.confirmTitle": "¿Borrar definitivamente?",
  "gallery.trash.confirmBody": "Se borrarán también sus mensajes y versiones. No se puede deshacer.",


  "conversation.title": "Conversación",
  "conversation.thread.label": "Mensajes de la conversación",
  "conversation.log.label": "Historial de la conversación",
  "conversation.emptyTitle": "Empieza la conversación",
  "conversation.empty": "Todavía no hay mensajes. Escribe el primero para empezar.",
  "conversation.loadingMore": "Cargando más mensajes…",
  "conversation.message.person": "Tú",
  "conversation.message.system": "Sistema",
  "conversation.message.kind.input": "Tu mensaje",
  "conversation.message.kind.update": "Actualización",
  "conversation.message.kind.error": "Error",
  "conversation.message.kind.question": "Pregunta",
  "conversation.questions.generate": "Generar sin responder",
  "conversation.questions.canned": "Genera la versión con tus mejores supuestos.",
  "conversation.message.openVersion": "Ver la versión",
  "conversation.composer.label": "Escribe tu mensaje",
  "conversation.composer.placeholder": "Pide un cambio, por ejemplo: «Haz el saldo más grande y agrega un acceso a Yape»",
  "conversation.composer.hint": "Pulsa Ctrl+Intro (⌘+Intro en Mac) para enviar.",
  "conversation.composer.submit": "Enviar",
  "conversation.composer.busy": "Hay un turno en curso. Podrás enviar otro mensaje cuando termine.",
  "conversation.composer.failed": "No se pudo enviar el mensaje.",
  // OWNER DECISION PENDING: regenerate wording
  "conversation.composer.mode.label": "¿Qué quieres hacer con este mensaje?",
  "conversation.composer.mode.modify": "Modificar",
  "conversation.composer.mode.regenerate": "Regenerar desde cero",
  // OWNER DECISION PENDING: brief wording
  "conversation.composer.reviewBrief.label": "Revisar el resumen antes de dibujar",
  // OWNER DECISION PENDING: reference image wording
  "conversation.composer.image.label": "Adjuntar imagen de referencia",
  "conversation.composer.image.hint": "Opcional. Una sola imagen PNG o JPEG de hasta 5 MB.",
  "conversation.composer.image.remove": "Quitar imagen",
  "conversation.composer.image.notSupported": "Elige una imagen PNG o JPEG.",
  "conversation.composer.image.tooBig": "La imagen pesa más de 5 MB. Elige una más pequeña.",
  "conversation.composer.image.unreadable": "No se pudo leer el archivo. Prueba con otro.",
  // OWNER DECISION PENDING: trimmed-context wording
  "conversation.contextTrimmed": "Para responder se usó solo la parte más reciente de la conversación.",
  "conversation.interrupted":"El último turno se interrumpió. Puedes enviar otro mensaje.",
  "conversation.cancel": "Cancelar turno",
  "conversation.cancel.failed": "No se pudo cancelar el turno.",
  "conversation.cancel.hint": "Pulsa Esc para cancelar el turno.",
  "conversation.status.starting": "Enviando el mensaje…",
  "conversation.status.evaluating": "Estamos evaluando tu mensaje…",
  "conversation.status.running": "Generando la respuesta…",
  "conversation.status.cancelling": "Cancelando…",
  "conversation.status.succeeded": "Turno completado.",
  "conversation.status.failed": "El turno ha fallado.",
  "conversation.status.cancelled": "Turno cancelado.",
  "conversation.status.interrupted": "El turno se interrumpió.",
  "conversation.status.asked": "Necesitamos un poco más de información: responde a las preguntas para continuar.",
  // F-PRV-003: the stages of a run, by the role of its step, and what is said while it works.
  "conversation.progress.listLabel": "Etapas de la generación",
  "conversation.progress.stage.normalize": "Entendiendo tu pedido",
  "conversation.progress.stage.desktop": "Diseñando la estructura",
  "conversation.progress.stage.mobile": "Adaptando a móvil",
  "conversation.progress.stage.diagram": "Dibujando el recorrido",
  "conversation.progress.stage.assess": "Evaluando tu mensaje",
  "conversation.progress.stage.summarize": "Resumiendo el diseño",
  "conversation.progress.stage.working": "Trabajando en tu pedido",
  "conversation.progress.stage.finishing": "Revisando y entregando",
  "conversation.progress.state.done": "(hecho)",
  "conversation.progress.state.current": "(en curso)",
  "conversation.progress.state.pending": "(pendiente)",
  "conversation.progress.step": "Paso {current} de {total}",
  "conversation.progress.now": "Ahora: {stage}",
  "conversation.progress.reassure.first": "Estamos cuidando cada detalle de tu diseño.",
  "conversation.progress.reassure.second": "Cada paso parte del anterior para que el resultado sea coherente.",
  "conversation.progress.reassure.third": "Puedes quedarte aquí: verás el resultado en cuanto esté listo.",
  "conversation.progress.reassure.fourth": "Si cambias de idea, puedes cancelar el turno.",
  // OWNER DECISION PENDING: brief wording
  "conversation.brief.label": "Resumen para revisar",
  "conversation.brief.hint": "Puedes corregirlo antes de que dibujemos. Pulsa Ctrl+Intro (⌘+Intro en Mac) para confirmar.",
  "conversation.brief.submit": "Confirmar y dibujar",
  "conversation.brief.failed": "No se pudo confirmar el resumen.",
  // F-UIX-022: the sections `normalize@v2` asks the model for, one field each.
  "conversation.brief.section.lead": "Introducción",
  "conversation.brief.section.screen": "Pantalla",
  "conversation.brief.section.audience": "Para quién",
  "conversation.brief.section.content": "Contenido",
  "conversation.brief.section.actions": "Acciones",
  "conversation.brief.section.states": "Estados",
  "conversation.brief.section.outOfScope": "Fuera de alcance",
  "conversation.status.briefed": "El resumen está listo para que lo revises. Corrígelo si quieres y confirma para dibujar.",
  "conversation.thread.answerHint": "Responde a las preguntas en el cuadro de mensaje de abajo.",
  "conversation.edit.action": "Editar y reenviar",
  "conversation.edit.actionFor": "Editar y reenviar: {excerpt}",
  "conversation.edit.label": "Tu mensaje editado",
  "conversation.edit.hint": "Pulsa Ctrl+Intro (⌘+Intro en Mac) para reenviar.",
  "conversation.edit.submit": "Reenviar",
  "conversation.edit.failed": "No se pudo reenviar el mensaje.",
  "conversation.edit.of": "Edición de un mensaje anterior",
  "conversation.retry.action": "Reintentar",
  "conversation.retry.failed": "No se pudo reintentar el turno.",
  "conversation.retry.hint": "El último turno no terminó. Puedes volver a intentarlo con el mismo mensaje.",

  "diagram.brief.label": "Describe el recorrido que quieres dibujar",
  "diagram.estimating": "Calculando el coste…",
  "diagram.confirm.title": "Esta opción tiene coste",
  "diagram.confirm.body": "El recorrido de pantallas puede costar hasta {cost}. ¿Quieres continuar?",
  "diagram.result.ready": "El diagrama está listo.",
  "diagram.frame.title": "Diagrama del recorrido de usuario",

  "scope.label": "Ámbito",
  "scope.chip.label": "Ámbito: {name}",
  "scope.explain": "Dónde se guarda este mockup y quién puede verlo; su consumo cuenta para este ámbito.",
  "scope.personal": "Personal",
  "scope.personalOther": "Personal de otra persona",
  "scope.change": "Cambiar ámbito",
  "scope.move.explain": "Mover el mockup cambia dónde se guarda, quién lo ve y a qué presupuesto se carga su consumo.",
  "scope.move.only": "Solo tienes un ámbito disponible ({name}): para moverlo necesitas pertenecer a otro equipo u organización.",
  "scope.changed": "El ámbito se ha cambiado.",
  "scope.failed": "No se pudo cambiar el ámbito.",

  "lienzo.title": "Lienzo",
  "editor.divider.label": "Ancho de la conversación. Flechas para ajustar, doble clic para restablecer",
  "editor.name.label": "Nombre del mockup (editable)",
  "editor.scope.chip": "Ámbito del mockup",
  "editor.profile.chip": "Perfil de diseño aplicado",
  "editor.profile.chip.label": "Diseño: {name}",
  "editor.title": "Lienzo: {name}",
  "editor.drawer.toggle": "Diagrama y detalles",
  "editor.drawer.tabs": "Paneles",
  "editor.drawer.diagram": "Diagrama",
  "editor.drawer.details": "Detalles",
  "editor.share.notAllowed": "No puedes compartir este mockup.",
  "editor.share.noVersion": "Todavía no se puede compartir: genera primero una versión.",
  "editor.conversation.collapse": "Contraer conversación (Mayús+\\)",
  "editor.conversation.expand": "Expandir conversación (Mayús+\\)",
  "editor.conversation.turnOpen": "Generando una respuesta",
  "editor.conversation.unread": "Hay mensajes nuevos",
  "editor.notFound.title": "No encontramos este mockup",
  "editor.notFound.body": "Puede que se haya borrado o que no tengas acceso. Búscalo en la galería o crea uno nuevo.",
  "lienzo.hero.title": "¿Qué pantalla quieres crear?",
  "lienzo.hero.section": "Crear un mockup",
  "lienzo.hero.subtitle": "Describe la pantalla y la dibujamos",
  "lienzo.canvas.title": "Tu pantalla aparecerá aquí",
  "lienzo.canvas.body": "Cuando crees el mockup verás aquí la primera versión, lista para probar en escritorio, tablet o móvil.",
  "lienzo.canvas.label": "Vista previa",
  "lienzo.new": "Nuevo",
  "lienzo.newMockup": "Nuevo mockup",
  "lienzo.suggest.label": "Sugerencias para empezar",
  "lienzo.suggest.banking": "Panel de banca personal",
  "lienzo.suggest.signup": "Formulario de registro con validación",
  "lienzo.suggest.landing": "Landing de campaña",
  "lienzo.suggest.sales": "Tablero de ventas por región",
  "lienzo.suggest.checkout": "Checkout con tarjeta y Yape",
  "lienzo.hero.placeholder": "Describe la pantalla que quieres crear…",
  "lienzo.hero.namePlaceholder": "Nombre (opcional)",

  "create.title": "Nuevo mockup",
  "create.message.label": "Describe la pantalla que quieres crear",
  "create.name.label": "Nombre (opcional)",
  "create.name.hint": "Si lo dejas vacío, se propondrá uno a partir de tu descripción.",
  "create.submit": "Crear mockup",
  "create.created": "El mockup se ha creado.",
  "create.created.unknown": "El mockup se ha creado, pero no sabemos cuál es: actualiza la galería para encontrarlo. Tu mensaje sigue aquí.",
  "create.failed": "No se pudo crear el mockup.",
  "create.scope.only":
    "Por ahora es el único ámbito donde puedes crear. Los demás salen de las organizaciones y los equipos a los que perteneces.",
  "create.scope.only.label": "Ámbito del mockup (el único disponible)",

  "versions.title": "Versiones",
  "versions.empty": "Todavía no hay versiones. Aparecerán al generar.",
  "versions.node": "Versión {n}",
  "versions.current": "Versión actual",
  "versions.created": "Creada",
  "editor.versions.chip": "v{n}",
  "editor.versions.none": "Sin versiones",
  "editor.versions.previewOf": "Vista previa de la v{n}",
  "editor.versions.use": "Usar esta versión",
  "editor.versions.compare": "Comparar",
  "editor.versions.compareWith": "Comparar la v{n} con la actual",
  "editor.compare.title": "Comparación: v{from} y v{to} (actual)",
  "editor.compare.label": "Comparación de versiones",
  "editor.compare.chosen": "v{n}",
  "editor.compare.current": "v{n} (actual)",
  "editor.compare.frame": "Vista previa de la v{n}",
  "editor.compare.code": "Ver diferencias de código",
  "editor.compare.close": "Cerrar la comparación (Esc)",
  "editor.compare.loading": "Cargando la comparación…",
  "editor.versions.viewing": "Estás viendo la v{n}",
  "editor.versions.backToCurrent": "Volver a la actual",

  "mockup.placeholder.title": "Mockup",
  "mockup.placeholder.body": "Genera una versión y consulta su historial.",
  "mockup.detail.label": "Detalle del mockup",
  "mockup.detail.created": "Creado",
  "mockup.detail.updated": "Actualizado",
  "mockup.detail.rename": "Renombrar",
  "mockup.detail.nameLabel": "Nombre del mockup",
  "mockup.detail.save": "Guardar",
  "mockup.detail.cancel": "Cancelar",
  "mockup.detail.renamed": "El nombre se ha guardado.",
  "mockup.detail.renameFailed": "No se pudo cambiar el nombre.",
  "mockup.detail.nameEmpty": "Escribe un nombre.",
  "mockup.detail.nameTooLong": "El nombre admite como máximo {max} caracteres.",
  "mockup.detail.tags": "Etiquetas",
  "mockup.detail.noTags": "Sin etiquetas",
  "mockup.detail.tagLabel": "Nueva etiqueta",
  "mockup.detail.addTag": "Añadir etiqueta",
  "mockup.detail.removeTag": "Quitar la etiqueta {tag}",
  "mockup.detail.tagAdded": "Etiqueta añadida.",
  "mockup.detail.tagRemoved": "Etiqueta quitada.",
  "mockup.detail.tagFailed": "No se pudo guardar la etiqueta.",
  "mockup.detail.tagEmpty": "Escribe una etiqueta.",
  "mockup.detail.tagTooLong": "Una etiqueta admite como máximo {max} caracteres.",
  "mockup.detail.tagDots": "Una etiqueta no puede ser «.» ni «..».",
  "mockup.detail.tagSlash": "Una etiqueta no puede llevar «/».",

  "version.title": "Versión",
  "version.heading": "Versión {n}",
  "version.back": "Volver al mockup",
  "version.switch.label": "Cambiar de versión",
  "version.switch.previous": "Anterior: versión {n}",
  "version.switch.next": "Siguiente: versión {n}",
  "version.switch.hint": "Pulsa Alt+Mayús+← o Alt+Mayús+→ (⌥+⇧ en Mac) para cambiar de versión.",
  "version.flow": "Flujo",
  "version.created": "Creada",
  "version.note": "Nota",
  "version.noNote": "Sin nota",
  "version.artifacts": "Piezas",
  "version.artifact.htmlBody": "HTML",
  "version.artifact.css": "CSS",
  "version.artifact.js": "JavaScript",
  "version.artifact.diagram": "Diagrama",
  "version.diagramPreview": "Vista previa del diagrama",
  "version.promote": "Usar como versión actual",
  "version.promoted": "Esta versión es ahora la actual.",
  "version.promote.failed": "No se pudo cambiar la versión actual.",
  "version.continue": "Continuar desde esta versión",
  "version.continued": "Seguirás trabajando desde esta versión: tus próximos cambios parten de ella.",
  "version.continue.failed": "No se pudo continuar desde esta versión.",
  "version.download.title": "Descargas",
  "version.download.html": "Descargar HTML",
  "version.download.zip": "Descargar paquete ZIP",
  "version.download.diagram": "Descargar diagrama",
  "version.download.failed": "No se pudo descargar el archivo.",

  "compare.title": "Comparar con otra versión",
  "compare.pick.label": "Versión con la que comparar",
  "compare.pick.none": "Elige una versión",
  "compare.none": "No hay otras versiones con las que comparar.",
  "compare.heading": "Cambios de la versión {from} a la versión {to}",
  "compare.identical": "Sin diferencias",

  "preview.frame.title": "Vista previa del mockup",
  "preview.stage.label": "Lienzo de la vista previa",
  "preview.empty": "Aún no hay ninguna versión que mostrar.",
  "preview.emptyBody": "Cuando envíes tu primer mensaje verás la pantalla generada en este lienzo.",
  "preview.interrupted.failed": "La generación no terminó. Seguimos mostrando la versión anterior.",
  "preview.interrupted.cancelled": "Cancelaste la generación. Seguimos mostrando la versión anterior.",
  "preview.interrupted.lost": "Se perdió la conexión y no pudimos confirmar que la generación terminara. Seguimos mostrando la versión anterior.",
  "preview.interrupted.emptyFailed": "La generación no terminó y todavía no hay ninguna versión que mostrar.",
  "preview.toolbar.label": "Controles de la vista previa",
  "preview.tabs.label": "Contenido de la vista previa",
  "preview.tabs.preview": "Vista previa",
  "preview.tabs.code": "Código",
  "preview.tabs.console": "Consola",
  "preview.openTab": "Abrir en una pestaña nueva",
  "preview.viewport.group": "Ancho de la vista previa",
  "preview.viewport.desktop": "Escritorio",
  "preview.viewport.tablet": "Tablet 768",
  "preview.viewport.mobile": "Móvil 375",
  "preview.viewport.free": "Libre",
  "preview.viewport.freeWidth": "Ancho en píxeles",
  "preview.zoom.label": "Zoom",
  "preview.zoom.out": "Reducir zoom",
  "preview.zoom.in": "Aumentar zoom",
  "preview.reload": "Recargar vista previa",
  "preview.fullscreen.enter": "Pantalla completa",
  "preview.fullscreen.exit": "Salir de pantalla completa",
  "preview.download": "Descargar HTML",
  "preview.console.title": "Consola de la vista previa",
  "preview.console.empty": "Aquí aparecen los mensajes y errores del código del mockup. Si está vacía, todo funciona.",
  "preview.console.tabHint": "Mensajes y errores que envía el código del mockup mientras se ejecuta",
  "preview.console.level.error": "Error",
  "preview.console.level.warn": "Aviso",
  "preview.source.title": "Código de esta versión",
  "preview.source.tabs": "Piezas del código",
  "preview.source.tab.html": "HTML",
  "preview.source.tab.css": "CSS",
  "preview.source.tab.js": "JavaScript",
  "preview.source.loading": "Cargando el código de esta versión.",
  "preview.source.empty": "Esta pieza está vacía.",
  "preview.source.failed": "No pudimos leer el código de esta versión.",
  "preview.source.retry": "Volver a leer el código",
  "preview.source.plain": "El código es muy largo y se muestra sin colores.",
  "preview.source.copy": "Copiar esta pieza",
  "preview.source.copyAll": "Copiar todo el código",
  "preview.source.copied": "Copiado",
  "preview.source.copyFailed": "No pudimos copiar. Selecciona el texto y cópialo tú.",

  "design.page.title": "Diseño",
  "design.editor.pickerFor": "{role}: selector de color",
  "design.page.subtitle": "Perfiles de colores, tipografía y logo que se aplican al generar.",
  "design.guide.title": "Cómo se usan los perfiles de diseño",
  "design.guide.body": "Un perfil de diseño hace que los mockups se parezcan a tu marca: el modelo toma como referencia su paleta, su tipografía, su logo y sus imágenes al generar.",
  "design.guide.first": "Crea un perfil con tu paleta, tipografía y logo.",
  "design.guide.second": "Úsalo por defecto en un ámbito (tu espacio personal, un equipo o la organización).",
  "design.guide.third": "Los mockups nuevos de ese ámbito lo toman como referencia; los que ya existen no cambian.",
  "design.guide.open": "¿Cómo funciona?",
  "design.help.palette": "Cada color tiene un papel: el principal para botones y enlaces, el fondo y las superficies para el lienzo, el texto para leer. El resumen de contraste dice si cada pareja se lee bien.",
  "design.help.type": "La tipografía de los títulos y la del texto, su tamaño base y cómo crecen los títulos.",
  "design.help.radius": "Qué tan redondeadas se ven las esquinas de botones, tarjetas y campos.",
  "design.help.instructions": "Indicaciones en palabras para el modelo, por ejemplo el tono de la marca o qué evitar.",
  "design.preview.title": "Vista previa en vivo",
  "design.preview.description": "Ejemplo de pantalla dibujada con los colores, la tipografía y las esquinas del perfil. Es solo una muestra.",
  "design.preview.heading": "Abre tu cuenta en 5 minutos",
  "design.preview.body": "Sin papeleo y con tu DNI. Recibe tu tarjeta en casa.",
  "design.preview.primary": "Empezar ahora",
  "design.preview.secondary": "Ver requisitos",
  "design.preview.input": "Correo electrónico",
  "design.preview.tile": "Cuenta Sueldo",
  "design.preview.tag": "Nuevo",
  "design.preview.success": "Aprobado",
  "design.preview.warning": "Pendiente",
  "design.preview.danger": "Rechazado",
  "design.list.title": "Perfiles de diseño",
  "design.list.new": "Nuevo perfil",
  "design.list.edit": "Editar",
  "design.list.activeIn": "Activo en: {scopes}",
  "design.list.unused": "Sin usar",
  "design.list.systemDefault": "Diseño por defecto del sistema",
  "design.list.readOnly": "Solo lectura",
  "design.list.empty": "Todavía no tienes perfiles de diseño propios.",
  "design.list.emptyBody": "Crea uno con tu paleta, tipografía y logo para que tus mockups se parezcan a tu marca.",
  "design.list.createFirst": "Crear tu primer perfil",
  "design.list.failed": "No se pudieron cargar los perfiles de diseño.",
  "design.editor.titleNew": "Nuevo perfil",
  "design.editor.titleEdit": "Editar perfil",
  "design.editor.version": "Versión {version}",
  "design.editor.savedBy": "guardada el {date} por {name}",
  "design.editor.savedOn": "guardada el {date}",
  "design.editor.stateColours": "Colores de estado",
  "design.editor.more": "Más opciones",
  "design.editor.typography": "Tipografía",
  "design.editor.references": "Imágenes de referencia",
  "design.editor.logo": "Logo",
  "design.editor.logoLater": "Podrás añadir el logo y las imágenes de referencia cuando guardes el perfil.",
  "design.help.logo": "El logo aparece en los mockups; las imágenes de referencia orientan el estilo visual que el modelo imita. Subir una imagen guarda una versión nueva con los valores ya guardados.",
  "design.contrast.help": "Cada pareja de colores debe llegar al menos a 4.5:1 (nivel AA) para que se lea bien; si alguna no llega, no se puede guardar.",
  "design.font.custom": "Personalizada",
  "design.editor.currentVersion": "Versión actual",
  "design.editor.name": "Nombre",
  "design.editor.headingFont": "Tipografía de los títulos",
  "design.editor.bodyFont": "Tipografía del texto",
  "design.editor.baseSize": "Tamaño base",
  "design.editor.pixels": "px",
  "design.editor.scale": "Escala tipográfica",
  "design.editor.register": "Registro visual",
  "design.editor.density": "Densidad",
  "design.editor.radius": "Esquinas",
  "design.editor.instructions": "Instrucciones de diseño",
  "design.editor.palette": "Paleta de colores",
  "design.editor.create": "Crear perfil",
  "design.editor.save": "Guardar versión",
  "design.editor.saved": "Se guardó la versión",
  "design.editor.failed": "No se pudo guardar el perfil de diseño.",
  "design.editor.loadFailed": "No se pudo cargar el perfil de diseño.",
  "design.font.systemSans": "Sans del sistema",
  "design.font.systemSerif": "Serif del sistema",
  "design.font.systemRounded": "Redondeada del sistema",
  "design.font.systemMono": "Monoespaciada del sistema",
  "design.scale.minorThird": "Tercera menor 1.2",
  "design.scale.majorThird": "Tercera mayor 1.25",
  "design.scale.perfectFourth": "Cuarta justa 1.333",
  "design.register.minimal": "Minimalista",
  "design.register.dense": "Denso",
  "design.register.editorial": "Editorial",
  "design.register.corporate": "Corporativo",
  "design.density.compact": "Compacta",
  "design.density.regular": "Normal",
  "design.density.comfortable": "Amplia",
  "design.radius.none": "Sin redondeo",
  "design.radius.small": "Pequeño",
  "design.radius.medium": "Mediano",
  "design.radius.large": "Grande",
  "design.role.primary": "Principal",
  "design.role.surface": "Fondo",
  "design.role.text": "Texto",
  "design.role.success": "Éxito",
  "design.role.warning": "Advertencia",
  "design.role.danger": "Peligro",
  "design.pair.textOnSurface": "Texto sobre el fondo",
  "design.pair.surfaceOnPrimary": "Fondo sobre el color principal",
  "design.pair.successOnSurface": "Éxito sobre el fondo",
  "design.pair.warningOnSurface": "Advertencia sobre el fondo",
  "design.pair.dangerOnSurface": "Peligro sobre el fondo",
  "design.contrast.title": "Contraste",
  "design.contrast.pass": "AA",
  "design.contrast.fail": "No cumple",
  "design.contrast.invalid": "Color no válido",
  "design.contrast.blocked": "No se puede guardar: contraste insuficiente en",
  "design.contrast.refused": "El servidor rechazó la paleta por contraste insuficiente en",
  "design.references.hint": "PNG, JPEG o WebP de hasta 512 KB. Subir una imagen guarda una versión nueva con los valores ya guardados.",
  "design.references.limits": "PNG, JPEG o WebP, hasta 512 KB.",
  "design.references.slot.image": "Imagen de referencia {number}",
  "design.references.file": "Elige un archivo",
  "design.references.alt": "Texto alternativo",
  "design.references.altHint": "Describe la imagen en una frase (hasta 200 caracteres).",
  "design.references.upload": "Subir imagen",
  "design.references.uploaded": "Se guardó la versión {version} con la imagen.",
  "design.references.failed": "No se pudo subir la imagen.",
  "design.references.refusal.notSupported": "Solo se admiten imágenes PNG, JPEG o WebP.",
  "design.references.refusal.tooBig": "La imagen supera el tamaño máximo de 512 KB.",
  "design.references.refusal.unreadable": "No se pudo leer el archivo.",
  "design.references.altRequired": "Escribe un texto alternativo para la imagen.",
  "design.references.altTooLong": "El texto alternativo admite como máximo 200 caracteres.",
  "design.editor.scope": "Ámbito del perfil",
  "design.scope.personal": "Personal",
  "design.scope.team": "Equipo",
  "design.scope.organization": "Organización",
  "design.scopeChip.description": "Diseño que se aplicará",
  "design.scopeChip.label": "Diseño: {name}",
  "design.scopeChip.title": "Diseño de los mockups nuevos",
  "design.scopeChip.explain": "El diseño no se elige al crear un mockup: se aplica el perfil activo de su ámbito. Cambiarlo solo afecta a los mockups que se creen después.",
  "design.scopeChip.from.personal": "Viene de tu perfil activo personal.",
  "design.scopeChip.from.team": "Viene del perfil activo del equipo.",
  "design.scopeChip.from.organization": "Viene del perfil activo de la organización.",
  "design.scopeChip.from.system": "Ningún perfil está activo en este ámbito: se usa el diseño por defecto del sistema.",
  "design.scopeChip.readOnly": "Pide a quien administra este ámbito que use otro perfil por defecto.",
  "design.scopeChip.noProfiles": "Este ámbito todavía no tiene perfiles que usar por defecto.",
  "design.scopeChip.manage": "Gestionar diseños",
  "design.active.choose": "Perfil para usar por defecto",
  "design.active.useDefault": "Usar por defecto en {scope}",
  "design.active.useDefaultMeans": "Usar un perfil por defecto en un ámbito significa que los mockups nuevos de ese ámbito lo toman como referencia. Los que ya existen no cambian.",
  "design.profile.what": "Un perfil de diseño reúne la paleta, la tipografía, el logo y las imágenes que el modelo toma como referencia al generar.",
  "design.indicator.appliedWhen": "Se aplicó al generar la versión de este mockup; cambiar el perfil por defecto del ámbito no cambia los mockups que ya existen.",
  "design.active.activated": "{name} se usa ahora por defecto.",
  "design.active.failed": "No se pudo cambiar el perfil activo.",
  "design.activeIn.label": "Activo en",
  "design.activeIn.hint": "Los mockups nuevos de ese ámbito usarán este perfil. Los existentes no cambian.",
  "design.activeIn.readOnly": "{scope} (solo lectura)",
  "design.indicator.title": "Diseño aplicado",
  "design.indicator.systemDefault": "Se aplica el diseño por defecto del sistema.",
  "design.indicator.version": "Versión",
  "design.indicator.level": "Nivel",
  "design.indicator.newer": "Hay una versión más nueva del perfil:",
  "design.indicator.repin": "Usar la versión más nueva",
  "design.indicator.repinned": "El mockup usa ahora la versión",
  "design.indicator.failed": "No se pudo cargar el diseño aplicado.",
  "design.indicator.repinFailed": "No se pudo cambiar la versión del diseño.",
  "design.indicator.cascade": "Cómo se elige el diseño",
  "design.level.personal": "Personal",
  "design.level.team": "Equipo",
  "design.level.organization": "Organización",
  "design.level.system": "Sistema",
  "design.level.unknown": "Nivel no reconocido",
  "design.outcome.chosen": "Aporta el diseño",
  "design.outcome.noActiveProfile": "Sin perfil activo",
  "design.outcome.archived": "Su perfil activo está archivado",
  "design.outcome.notInMockupScope": "No se aplica a este mockup",
  "design.outcome.notReached": "No se consultó: un nivel anterior ya aportó el diseño",
  "design.outcome.notVisible": "No visible para ti",
  "design.outcome.unknown": "Resultado no reconocido",

  "sharing.title": "Enlaces para compartir",
  "sharing.open": "Compartir",
  "sharing.dialog.title": "Compartir",
  "sharing.artifact.label": "Qué",
  "sharing.artifact.prototype": "Prototipo",
  "sharing.artifact.diagram": "Diagrama",
  "sharing.mode.label": "Versión",
  "sharing.mode.live": "Siempre la última",
  "sharing.mode.frozen": "Fijar una versión",
  "sharing.version.label": "Versión fija",
  "sharing.version.option": "Versión {n}",
  "sharing.version.current": "Versión {n} (actual)",
  "sharing.version.loading": "Cargando las versiones…",
  "sharing.version.failed": "No se pudieron leer las versiones.",
  "sharing.expiry.label": "Caducidad (días)",
  "sharing.expiry.hint": "Un número entero de días, desde 1. Déjalo vacío para usar el plazo por defecto.",
  "sharing.expiry.placeholder": "Por defecto",
  "sharing.expiry.invalid": "Escribe un número entero de días, desde 1, o deja el campo vacío.",
  "sharing.label.label": "Etiqueta (opcional)",
  "sharing.label.hint": "Solo la ven quienes gestionan el mockup; no forma parte del enlace.",
  "sharing.create": "Crear enlace",
  "sharing.failed": "No se pudo crear el enlace.",
  "sharing.disabled": "Compartir enlaces no está disponible en este momento.",
  "sharing.created.title": "Enlace creado",
  "sharing.created.once": "Por seguridad este enlace solo se muestra ahora. Cópialo antes de cerrar.",
  "sharing.created.field": "Enlace para compartir",
  "sharing.copy": "Copiar enlace",
  "sharing.copied": "Enlace copiado",
  "sharing.copyManual": "No pudimos copiar. El enlace está seleccionado: cópialo tú.",
  "sharing.list.title": "Enlaces de este mockup",
  "sharing.list.empty": "Todavía no hay enlaces.",
  "sharing.list.loading": "Cargando los enlaces…",
  "sharing.list.failed": "No se pudieron leer los enlaces.",
  "sharing.list.noLabel": "Sin etiqueta",
  "sharing.list.expires": "Caduca el {day}",
  "sharing.state.active": "Activo",
  "sharing.state.expired": "Caducado",
  "sharing.state.revoked": "Revocado",
  "sharing.state.suspended": "Suspendido",
  "sharing.state.unknown": "Estado no reconocido",
  "sharing.revoke": "Revocar",
  "sharing.revoke.for": "Revocar el enlace {name}",
  "sharing.revoke.title": "¿Revocar este enlace?",
  "sharing.revoke.body": "Quien tenga el enlace dejará de ver el mockup en cuanto lo revoques. No se puede deshacer.",
  "sharing.revoke.failed": "No se pudo revocar el enlace.",
  "sharing.password.label": "Contraseña",
  "sharing.password.hint": "Quien abra el enlace tendrá que escribirla. Déjala vacía para no pedir ninguna.",
  "sharing.password.toggle": "Proteger con contraseña",
  "sharing.again": "Crear otro",
  "sharing.manageAll": "Gestionar todos los enlaces",
  "sharing.activeCount": "{n} activos",
  "sharing.manageArrow": "→",
  "sharing.unavailable": "Disponible cuando exista la primera versión.",
  "sharing.password.clear": "Quitar la contraseña",
  "sharing.created.protected": "El enlace pide la contraseña que elegiste.",
  "sharing.created.unprotected": "El enlace se creó sin contraseña.",
  "sharing.created.unprotectedNext": "Ponla desde Enlaces compartidos antes de compartirlo, o revócalo.",
  "sharing.panel.title": "Enlaces compartidos",
  "sharing.panel.subtitle": "Todos los enlaces que has creado. También puedes gestionarlos desde el botón Compartir de cada mockup.",
  "sharing.panel.more": "Ver más enlaces",
  "sharing.panel.neverOpened": "Nunca",
  "sharing.panel.protected": "Con contraseña",
  "sharing.panel.mode.live": "Última versión",
  "sharing.panel.mode.frozen": "Versión fija",
  "sharing.tabs.label": "Estado",
  "sharing.tab.all": "Todos",
  "sharing.tab.active": "Activos",
  "sharing.tab.expired": "Caducados",
  "sharing.tab.revoked": "Revocados",
  "sharing.artifact.all": "Todos los artefactos",
  "sharing.col.mockup": "Mockup",
  "sharing.col.artifact": "Artefacto",
  "sharing.col.mode": "Modo",
  "sharing.col.expires": "Caducidad",
  "sharing.col.protected": "Protegido",
  "sharing.col.opens": "Aperturas",
  "sharing.col.lastOpened": "Último acceso",
  "sharing.col.state": "Estado",
  "sharing.col.actions": "Acciones",
  "sharing.empty.title": "Todavía no has compartido ningún mockup",
  "sharing.empty.body": "Abre un mockup y usa el botón Compartir para crear tu primer enlace.",
  "sharing.empty.action": "Ir a la galería",
  "sharing.noState.title": "No hay enlaces en este estado",
  "sharing.noState.body": "Cambia el filtro para ver el resto.",
  "sharing.navActive": "{n} enlaces activos",
  "sharing.row.menu": "Más acciones: {name}",
  "sharing.row.open": "Abrir el mockup",
  "sharing.edit.menu": "Cambiar caducidad o contraseña",
  "sharing.edit.title": "Cambiar el enlace {name}",
  "sharing.edit.expiry": "Nueva caducidad, en días desde hoy",
  "sharing.edit.expiryHint": "Déjalo vacío para no cambiarla.",
  "sharing.edit.passwordHint": "Déjala vacía para no cambiarla.",
  "sharing.edit.save": "Guardar cambios",
  "sharing.edit.nothing": "No hay nada que cambiar.",
  "sharing.edit.failed": "No se pudo cambiar el enlace.",

  "admin.page.totals.subtitle": "Generaciones, tokens y costo de todas las organizaciones.",
  "admin.page.models.subtitle": "Qué modelo usa cada nivel y cuánto puede costar un turno.",
  "admin.page.status.subtitle": "La base de datos, el proveedor de modelos, las colas y las tareas programadas.",
  "admin.totals.title": "Totales de la plataforma",
  "admin.totals.tableLabel": "Totales por organización",
  "admin.totals.organization": "Organización",
  "admin.totals.delivered": "Entregadas",
  "admin.totals.failed": "Fallidas",
  "admin.totals.cancelled": "Canceladas",
  "admin.totals.tokens": "Tokens",
  "admin.totals.cost": "Costo",
  "admin.totals.detail": "Detalle",
  "admin.totals.showDetail": "Ver detalle",
  "admin.totals.hideDetail": "Ocultar detalle",
  "admin.totals.promptTokens": "Tokens de entrada",
  "admin.totals.completionTokens": "Tokens de salida",
  "admin.totals.costs": "Costo por moneda",
  "admin.totals.noOrganization": "Sin organización",
  "admin.totals.total": "Total",
  "admin.totals.noCost": "Sin costo",
  "admin.totals.range": "Del {from} al {to}",
  "admin.totals.rangeLabel": "Período",
  "admin.totals.from": "Desde",
  "admin.totals.to": "Hasta",
  "admin.totals.rangeHint": "Déjalas vacías para ver los últimos 366 días.",
  "admin.totals.apply": "Aplicar",
  "admin.totals.loading": "Cargando los totales…",
  "admin.totals.empty": "No hubo generaciones en este período.",
  "admin.totals.tiles": "Resumen del período",
  "admin.totals.tile.cost": "Costo total",
  "admin.totals.tile.organizations": "{count} organizaciones con consumo",
  "admin.totals.tile.generations": "Generaciones",
  "admin.totals.tile.delivered": "{count} entregadas",
  "admin.totals.tile.errorRate": "Tasa de error",
  "admin.totals.tile.failures": "{failed} fallidas · {cancelled} canceladas",
  "admin.totals.tile.tokens": "Tokens",
  "admin.totals.tile.tokenSplit": "{prompt} de entrada · {completion} de salida",
  "admin.totals.chartTitle": "Generaciones por organización",
  "admin.totals.chartItem": "{label}: {value} generaciones.",

  "admin.status.title": "Estado del sistema",
  "admin.status.loading": "Cargando el estado del sistema…",
  "admin.status.readAt": "Leído: {at}",
  "admin.status.refresh": "Actualizar",
  "admin.status.refreshFailed": "No se pudo actualizar: se muestra la lectura anterior.",
  "admin.status.summaryLabel": "Resumen del estado",
  "admin.status.unknown": "Sin dato",
  "admin.status.database": "Base de datos",
  "admin.status.database.ok": "Disponible",
  "admin.status.database.unavailable": "No disponible",
  "admin.status.provider": "Proveedor de modelos",
  "admin.status.provider.ok": "Disponible",
  "admin.status.provider.failing": "Con fallos",
  "admin.status.provider.unknown": "Sin datos",
  "admin.status.provider.checked": "Comprobado: {at}",
  "admin.status.provider.never": "Aún no se ha comprobado.",
  "admin.status.caps": "Límites de uso",
  "admin.status.caps.on": "Activos",
  "admin.status.caps.off": "Desactivados",
  "admin.status.limiter": "Limitadores por minuto",
  "admin.status.limiter.ok": "Funcionando",
  "admin.status.limiter.down": "Sin almacén compartido",
  "admin.status.limiter.since": "Dejan pasar todas las solicitudes desde el {at} (vista de esta réplica).",
  "admin.status.outbox.title": "Cola de eventos",
  "admin.status.outbox.pending": "Pendientes",
  "admin.status.outbox.oldest": "Espera del más antiguo",
  "admin.status.outbox.none": "Nada pendiente",
  "admin.status.outbox.failed": "Fallidos",
  "admin.status.jobs.title": "Tareas programadas",
  "admin.status.jobs.name": "Tarea",
  "admin.status.jobs.interval": "Cada",
  "admin.status.jobs.lastRun": "Última ejecución",
  "admin.status.jobs.nextRun": "Próxima ejecución",
  "admin.status.jobs.state": "Estado",
  "admin.status.jobs.never": "Nunca",
  "admin.status.jobs.onTime": "Al día",
  "admin.status.jobs.behind": "Atrasada",
  "admin.status.jobs.empty": "No hay tareas programadas registradas.",
  "admin.status.jobs.outcome": "Última ejecución",
  "admin.status.jobs.outcome.succeeded": "Correcta",
  "admin.status.jobs.outcome.failed": "Falló",
  "admin.status.jobs.outcome.none": "Sin ejecutar",
  "admin.status.jobs.outcome.unknown": "Sin datos",
  "admin.status.jobs.failing": "Tareas cuya última ejecución falló: {count}.",
  "admin.status.job.heartbeat": "Latido del proceso de tareas",
  "admin.status.job.personalDataPurge": "Purga de datos personales",
  "admin.status.job.mockupTrashPurge": "Vaciado de la papelera",
  "admin.status.job.versionRetentionPurge": "Retención de versiones",
  "admin.status.job.shareRetentionPurge": "Retención de enlaces compartidos",
  "admin.status.job.outboxDispatch": "Envío de eventos",
  "admin.status.job.usageGlobalAlert": "Alerta de gasto global",
  "admin.status.job.providerProbe": "Comprobación del proveedor",
  "admin.status.job.generationStaleClose": "Cierre de generaciones colgadas",
  "admin.status.job.usageDailyAggregate": "Agregado diario de consumo",
  "admin.status.models.title": "Salud de los modelos en la última hora",
  "admin.status.models.model": "Modelo",
  "admin.status.models.role": "Paso",
  "admin.status.models.calls": "Llamadas",
  "admin.status.models.failedSteps": "Pasos fallidos",
  "admin.status.models.rate": "Tasa de pasos fallidos",
  "admin.status.models.median": "Duración p50",
  "admin.status.models.tail": "Duración p95",
  "admin.status.models.since": "Desde: {at}",
  "admin.status.models.note":
    "Cuenta los pasos que el modelo respondió y que terminaron con error. Los tiempos de espera y los errores 5xx del proveedor no dejan paso: se ven en el estado del proveedor.",
  "admin.status.models.empty": "Ningún modelo recibió llamadas en la última hora.",
  "admin.status.capsNotice.title": "Límites desactivados",
  "admin.status.capsNotice.body":
    "Los cupos y presupuestos de uso no se aplican: ninguna generación se rechaza por consumo.",
  "admin.status.alertNotice.title": "Alerta de gasto global",
  "admin.status.alertNotice.body": "El gasto de la plataforma en {month} superó el umbral de alerta.",
  "admin.status.creditsNotice.title": "Saldo del proveedor bajo",
  "admin.status.creditsNotice.body":
    "Quedan {amount} de saldo en el proveedor de modelos. Recarga créditos en OpenRouter para que las generaciones no se detengan.",
  "admin.status.creditsNotice.bodyUnknown":
    "El saldo del proveedor de modelos está bajo. Recarga créditos en OpenRouter para que las generaciones no se detengan.",
  "admin.status.credits.title": "Saldo del proveedor",
  "admin.status.credits.remaining": "Saldo restante",
  "admin.status.credits.state": "Estado del saldo",
  "admin.status.credits.state.ok": "Correcto",
  "admin.status.credits.state.low": "Bajo",
  "admin.status.credits.state.unknown": "Desconocido",
  "admin.status.credits.threshold": "Umbral de aviso",
  "admin.status.credits.checked": "Última consulta",
  "admin.status.credits.unavailable": "No se pudo consultar el saldo",
  "admin.ceilings.title": "Topes por organización",
  "admin.ceilings.intro":
    "El tope limita lo que el administrador de cada organización puede repartir entre sus personas y equipos cada mes.",
  "admin.ceilings.organization": "Organización",
  "admin.ceilings.choose": "Elige una organización",
  "admin.ceilings.tableLabel": "Topes por organización",
  "admin.ceilings.created": "Creada",
  "admin.ceilings.state": "Estado",
  "admin.ceilings.status.active": "Activa",
  "admin.ceilings.actions": "Acciones",
  "admin.ceilings.edit": "Editar topes",
  "admin.ceilings.editOf": "de {name}",
  "admin.ceilings.dialogTitle": "Topes de {name}",
  "admin.ceilings.listLoading": "Cargando las organizaciones…",
  "admin.ceilings.noOrganizations": "No hay organizaciones.",
  "admin.ceilings.loading": "Cargando el tope…",
  "admin.ceilings.legend": "Tope mensual",
  "admin.ceilings.generations": "Generaciones por persona al mes",
  "admin.ceilings.generationsHint": "Un número entero entre 0 y 100 000. Vacío: sin tope.",
  "admin.ceilings.generationsError": "Escribe un número entero entre 0 y 100 000, o déjalo vacío.",
  "admin.ceilings.budget": "Presupuesto por persona al mes (USD)",
  "admin.ceilings.budgetHint": "Un monto entre 0 y 1 000 000, con hasta 6 decimales. Vacío: sin tope.",
  "admin.ceilings.total": "Presupuesto total de la organización al mes (USD, opcional)",
  "admin.ceilings.totalHint":
    "Un monto entre 0 y 1 000 000, con hasta 6 decimales. Vacío: la suma de los presupuestos de sus personas.",
  "admin.ceilings.amountError": "Escribe un monto entre 0 y 1 000 000 con hasta 6 decimales, o déjalo vacío.",
  "admin.ceilings.thousandsError": "Escribe el importe sin separador de miles, por ejemplo 1000 o 1.5.",
  "admin.ceilings.preview": "Se guardará como {amount}.",
  "admin.ceilings.save": "Guardar tope",
  "admin.ceilings.saved": "Tope guardado.",
  "admin.ceilings.failed": "No se pudo guardar el tope.",
  "admin.ceilings.tighter":
    "Este tope es más estricto que el actual. Los límites por persona que lo superen se recortan al tope desde la próxima generación, y su administrador verá un aviso para ajustarlos. El presupuesto total no puede quedar por debajo de lo ya asignado a los equipos: si lo hace, no se guarda.",
  "admin.ceilings.exceeds":
    "El reparto actual de la organización supera el presupuesto disponible o este tope. Los límites por persona que superen el tope se recortan desde la próxima generación, y su administrador verá un aviso para ajustar el reparto. Lo asignado a los equipos no se recorta; solo se avisa para que lo ajusten.",
  "admin.ceilings.fits": "El reparto actual de la organización cabe en este tope: no se recorta ningún límite.",

  "admin.modelSettings.title": "Modelos de lenguaje",
  "admin.modelSettings.intro":
    "Elige qué modelo usa cada nivel, sus topes y sus precios. Los cambios valen para toda la plataforma desde el siguiente turno; un turno en curso no cambia.",
  "admin.modelSettings.loading": "Cargando los ajustes de modelos…",
  "admin.modelSettings.source": "Origen: {source}",
  "admin.modelSettings.source.env": ".env (valores de arranque)",
  "admin.modelSettings.source.panel": "Este panel",
  "admin.modelSettings.revision": "Revisión {revision}",
  "admin.modelSettings.updated": "Última modificación: {at}, por {name}.",
  "admin.modelSettings.updatedNoAuthor": "Última modificación: {at}.",
  "admin.modelSettings.estimated": "Costo máximo estimado por turno: {amount}.",
  "admin.modelSettings.estimatedNone": "Costo máximo estimado por turno: no disponible.",
  "admin.modelSettings.tier.fast": "Nivel rápido",
  "admin.modelSettings.tier.capable": "Nivel capaz",
  "admin.modelSettings.tier.fast.description": "Analiza la petición y redacta el resumen.",
  "admin.modelSettings.tier.capable.description": "Diseña y genera el código de la pantalla.",
  "admin.modelSettings.suggestion": "Entrada {prompt} · salida {completion} por millón de tokens",
  "admin.modelSettings.capUnit": "tokens",
  "admin.modelSettings.currency": "USD",
  "admin.modelSettings.prices.title": "Precios",
  "admin.modelSettings.bar": "Guardar los ajustes de modelos",
  "admin.modelSettings.cost.label": "Costo máximo estimado por turno:",
  "admin.modelSettings.cost.none": "no disponible",
  "admin.modelSettings.cost.before": "(antes {amount})",
  "admin.modelSettings.cost.preview": "Calcular con los cambios",
  "admin.modelSettings.cost.failed": "No se pudo calcular el costo con los cambios.",
  "admin.modelSettings.model": "Modelo",
  "admin.modelSettings.modelHint": "El identificador del modelo en el catálogo, por ejemplo proveedor/modelo.",
  "admin.modelSettings.cap": "Tope de salida (tokens)",
  "admin.modelSettings.capHint": "Un número entero mayor que 0. Con «Sin límite» no se envía tope al modelo.",
  "admin.modelSettings.noLimit": "Sin límite",
  "admin.modelSettings.effort": "Esfuerzo de razonamiento",
  "admin.modelSettings.effort.none": "Ninguno",
  "admin.modelSettings.effort.minimal": "Mínimo",
  "admin.modelSettings.effort.low": "Bajo",
  "admin.modelSettings.effort.medium": "Medio",
  "admin.modelSettings.effort.high": "Alto",
  "admin.modelSettings.prices.legend": "Precios (USD por millón de tokens)",
  "admin.modelSettings.prices.model": "Precios de {model}",
  "admin.modelSettings.prices.prompt": "Entrada de {model}",
  "admin.modelSettings.prices.completion": "Salida de {model}",
  "admin.modelSettings.prices.hint": "Un monto en dólares, por ejemplo 3 o 0.25. Todo modelo elegido necesita precio.",
  "admin.modelSettings.messages.legend": "Conversación",
  "admin.modelSettings.message": "Máximo de caracteres por mensaje",
  "admin.modelSettings.messageHint": "Un número entero mayor que 0. Con «Sin límite» se acepta cualquier mensaje que quepa en la petición.",
  "admin.modelSettings.limits.title": "Límites técnicos (solo lectura)",
  "admin.modelSettings.limits.deadline": "Tiempo máximo por llamada al modelo: {seconds} s.",
  "admin.modelSettings.limits.body": "Tamaño máximo de una petición: {size}.",
  "admin.modelSettings.limits.note": "Se configuran en el entorno del servidor, no desde este panel.",
  "admin.modelSettings.save": "Guardar cambios",
  "admin.modelSettings.saved": "Cambios guardados. Ahora es la revisión {revision}.",
  "admin.modelSettings.failed": "No se pudieron guardar los cambios.",
  "admin.modelSettings.catalogueDown":
    "No se pudo consultar el catálogo de modelos. No se guardó nada; inténtalo de nuevo en unos minutos.",
  "admin.modelSettings.fixFields": "Revisa los campos marcados: no se guardó nada.",
  "admin.modelSettings.conflict":
    "Otra persona guardó antes: ahora es la revisión {revision}. Tus cambios siguen en el formulario; revísalos y guárdalos de nuevo, o descártalos.",
  "admin.modelSettings.conflict.reload": "Descartar mis cambios y cargar la revisión {revision}",
  "admin.modelSettings.confirm.title": "Confirmar los cambios",
  "admin.modelSettings.confirm.cost": "Costo máximo estimado por turno con los valores nuevos: {amount}.",
  "admin.modelSettings.confirm.costLoading": "Calculando el costo máximo por turno con los valores nuevos…",
  "admin.modelSettings.confirm.costUnknown": "No se pudo calcular el costo con los valores nuevos. Puedes guardar igualmente o cancelar.",
  "admin.modelSettings.confirm.capChanged": "Cambias un tope: eso cambia lo que puede costar cada turno.",
  "admin.modelSettings.confirm.model": "Cambias el modelo de un nivel: con él cambia el costo de cada turno.",
  "admin.modelSettings.confirm.prices": "Cambias precios: con ellos se calcula lo que cuesta cada turno.",
  "admin.modelSettings.confirm.capUnset": "Quitas un tope: la aplicación ya no limitará ese valor.",
  "admin.modelSettings.confirm.conflict": "Guardar reemplazará lo que guardó la otra persona.",
  "admin.modelSettings.confirm.confirm": "Guardar",
  "admin.modelSettings.confirm.cancel": "Cancelar",
  "admin.modelSettings.restore": "Restaurar valores del entorno",
  "admin.modelSettings.restore.title": "¿Restaurar los valores del entorno?",
  "admin.modelSettings.restore.body":
    "Se guardará una revisión nueva con los valores del entorno del servidor (.env), y se descartarán los cambios sin guardar de este formulario. Los turnos en curso no cambian.",
  "admin.modelSettings.restore.confirm": "Restaurar",
  "admin.modelSettings.restored": "Valores del entorno restaurados. Ahora es la revisión {revision}.",
  "admin.modelSettings.history.title": "Historial de revisiones",
  "admin.modelSettings.history.loading": "Cargando el historial…",
  "admin.modelSettings.history.empty": "Todavía no hay revisiones guardadas.",
  "admin.modelSettings.history.item": "Revisión {revision}",
  "admin.modelSettings.history.by": "por {name}",
  "admin.modelSettings.history.noAuthor": "sin autor",
  "admin.modelSettings.history.changed": "Cambió: {fields}.",
  "admin.modelSettings.history.nothing": "Sin cambios de campos.",
  "admin.modelSettings.field.fast.model": "modelo rápido",
  "admin.modelSettings.field.fast.cap": "tope de salida rápido",
  "admin.modelSettings.field.fast.effort": "razonamiento rápido",
  "admin.modelSettings.field.capable.model": "modelo capaz",
  "admin.modelSettings.field.capable.cap": "tope de salida capaz",
  "admin.modelSettings.field.capable.effort": "razonamiento capaz",
  "admin.modelSettings.field.prices": "precios",
  "admin.modelSettings.field.message": "máximo de caracteres por mensaje",
  "admin.modelSettings.error.required": "Escribe el identificador del modelo.",
  "admin.modelSettings.error.positiveInteger": "Escribe un número entero mayor que 0, o activa «Sin límite».",
  "admin.modelSettings.error.sameModel": "El nivel capaz debe usar un modelo distinto del nivel rápido.",
  "admin.modelSettings.error.priceRequired": "Escribe el precio.",
  "admin.modelSettings.error.priceInvalid":
    "Escribe un monto válido en dólares, sin separador de miles, por ejemplo 3 o 0.25.",
  "admin.modelSettings.error.unknownModel": "El catálogo no conoce este modelo.",
  "admin.modelSettings.error.unpricedModel": "Este modelo no tiene precio: escríbelo.",
  "admin.modelSettings.error.aboveModelMaximum": "El valor supera el máximo que admite el modelo.",
  "admin.modelSettings.error.rejected": "El servidor no aceptó este valor.",
  "admin.modelSettings.error.tooManyPrices":
    "Se alcanzó el máximo de precios guardados: elige un modelo que ya tenga precio.",
  "admin.modelSettings.error.malformedModelId":
    "El identificador del modelo no es válido: escríbelo sin espacios ni caracteres invisibles.",

  "usage.notice.label": "Aviso de consumo",
  "usage.notice.person": "Te acercas al límite de tu cupo mensual de uso.",
  "usage.notice.team": "Tu equipo se acerca al límite de su presupuesto mensual.",
  "usage.notice.organization": "Tu organización se acerca al límite de su presupuesto mensual.",
  "usage.notice.link": "Mi consumo",
  "usage.refusal.person": "Alcanzaste tu cupo mensual de generaciones.",
  "usage.refusal.team": "Tu equipo alcanzó su presupuesto mensual.",
  "usage.refusal.organization": "Tu organización alcanzó su presupuesto mensual.",
  "usage.refusal.other": "Se alcanzó un límite de uso.",
  "usage.refusal.renews": "Se renueva el {date}.",
  "usage.refusal.contact": "Para continuar, comunícate con quien administra tu organización.",
  "usage.refusal.personalScope": "La generación de mockups personales no está disponible por ahora.",
  "backoffice.title": "Backoffice",
  "backoffice.nav.label": "Secciones del backoffice",
  "backoffice.meter.text": "Tu consumo: {delivered} de {limit} generaciones este mes",
  "backoffice.meter.unlimited": "Tu consumo: {delivered} generaciones este mes",
  "backoffice.meter.label": "Generaciones del mes",
  "backoffice.nav.myUsage": "Mi consumo",
  "backoffice.myUsage.title": "Mi consumo",
  "backoffice.myUsage.intro": "Tus generaciones y su coste. Solo tú ves estas cifras.",
  "backoffice.myUsage.loading": "Cargando tu consumo…",
  "backoffice.myUsage.month.title": "Este mes",
  "backoffice.myUsage.month.renews": "Tus límites se renuevan el {date}.",
  "backoffice.myUsage.month.notEnforced": "Los límites mensuales no se están aplicando; tu consumo se registra igual.",
  "backoffice.myUsage.month.empty": "Todavía no tienes un ámbito con límites este mes.",
  "backoffice.myUsage.scope.personal": "Personal",
  "backoffice.myUsage.scope.formerOrganization": "Una organización a la que ya no perteneces",
  "backoffice.myUsage.figure.generations": "Generaciones",
  "backoffice.myUsage.figure.generationsValue": "{used} de {limit}",
  "backoffice.myUsage.figure.inFlight": "En curso",
  "backoffice.myUsage.figure.spent": "Gasto",
  "backoffice.myUsage.figure.spentValue": "{spent} de {budget}",
  "backoffice.myUsage.figure.remaining": "Te queda",
  "backoffice.myUsage.figure.remainingValue": "{generations} generaciones y {budget}",
  "backoffice.myUsage.period.title": "Consumo del periodo",
  "backoffice.myUsage.period.range": "Del {from} al {to}.",
  "backoffice.myUsage.period.empty": "No generaste nada en este periodo.",
  "backoffice.myUsage.column.currency": "Moneda",
  "backoffice.myUsage.column.delivered": "Entregadas",
  "backoffice.myUsage.column.failed": "Fallidas",
  "backoffice.myUsage.column.cancelled": "Canceladas",
  "backoffice.myUsage.column.cost": "Coste",
  "backoffice.myUsage.column.mockup": "Mockup",
  "backoffice.myUsage.column.generations": "Generaciones",
  "backoffice.myUsage.mockups.title": "Coste por mockup",
  "backoffice.myUsage.mockups.emptyTitle": "Sin mockups con consumo",
  "backoffice.myUsage.mockups.emptyBody": "Cuando generes en un mockup, su coste aparecerá aquí.",
  "backoffice.myUsage.exports.intro": "Descarga tu consumo y tu coste por mockup, del mismo periodo que esta página.",
  "backoffice.myUsage.exports.consumption": "Descargar mi consumo",
  "backoffice.myUsage.exports.mockups": "Descargar mi coste por mockup",
  "backoffice.nav.consumption": "Consumo por ámbito",
  "backoffice.consumption.tiles.label": "Resumen del periodo",
  "backoffice.consumption.tile.spend": "Gasto del periodo",
  "backoffice.consumption.tile.delivered": "Entregadas",
  "backoffice.consumption.tile.failed": "Fallidas",
  "backoffice.consumption.tile.cancelled": "Canceladas",
  "backoffice.consumption.chart.title": "Gasto diario",
  "backoffice.consumption.chart.label": "Gasto diario en {currency}",
  "backoffice.consumption.chart.tableLabel": "Gasto diario en {currency}, en tabla",
  "backoffice.consumption.chart.asTable": "Ver como tabla",
  "backoffice.consumption.chart.day": "Día",
  "backoffice.consumption.chart.spend": "Gasto",
  "backoffice.consumption.currency.label": "Moneda",
  "backoffice.consumption.share": "Parte del total",
  "backoffice.consumption.empty.title": "Sin consumo en este periodo",
  "backoffice.consumption.title": "Consumo",
  "backoffice.consumption.intro": "Generaciones y coste de lo que administras o lideras. Solo lectura.",
  "backoffice.consumption.loading": "Cargando el consumo…",
  "backoffice.consumption.none.title": "No hay consumo que mostrarte aquí",
  "backoffice.consumption.none.body": "Esta sección es para quien administra una organización o lidera un equipo. Tu propio consumo está en «Mi consumo».",
  "backoffice.consumption.target.label": "Ámbito",
  "backoffice.consumption.target.organization": "Organización {name}",
  "backoffice.consumption.target.team": "Equipo {name}",
  "backoffice.consumption.target.unnamedOrganization": "Organización sin nombre",
  "backoffice.consumption.target.unnamedTeam": "Equipo sin nombre",
  "backoffice.consumption.grouping.label": "Agrupar por",
  "backoffice.consumption.grouping.day": "Día",
  "backoffice.consumption.grouping.team": "Equipo",
  "backoffice.consumption.grouping.person": "Persona",
  "backoffice.consumption.totals.title": "Totales del periodo",
  "backoffice.consumption.rows.title": "Detalle del periodo",
  "backoffice.consumption.empty": "No hubo generaciones en este periodo.",
  "backoffice.consumption.subject.noTeam": "Sin equipo",
  "backoffice.consumption.subject.unknownTeam": "Equipo no disponible",
  "backoffice.consumption.subject.noPerson": "Sin atribuir",
  "backoffice.consumption.mockups.emptyBody": "Cuando alguien genere en un mockup de este ámbito, su coste aparecerá aquí.",
  "backoffice.exports.title": "Exportar",
  "backoffice.exports.intro":
    "Descarga los datos de este ámbito para el mismo periodo que los informes; el consumo sale con la agrupación elegida arriba.",
  "backoffice.exports.format": "Formato",
  "backoffice.exports.format.csv": "CSV (hoja de cálculo)",
  "backoffice.exports.format.json": "JSON",
  "backoffice.exports.consumption": "Descargar consumo",
  "backoffice.exports.mockups": "Descargar costo por mockup",
  "backoffice.exports.costs": "Descargar costos por modelo y por versión de flujo",
  "backoffice.exports.costs.help":
    "Cada fila de este archivo es de un desglose (columna breakdown: model o flow_version) y de una moneda (columna currency). Suma cost_micros solo dentro del mismo desglose y la misma moneda, nunca entre ellos: los dos desgloses reparten el mismo gasto.",
  "backoffice.exports.failed": "No se pudo descargar el archivo.",
  "backoffice.nav.distribution": "Distribución",
  "backoffice.distribution.title": "Distribución",
  "backoffice.distribution.intro": "Los límites de cada persona y las asignaciones de cada equipo, dentro del tope de la organización.",
  "backoffice.distribution.loading": "Cargando la distribución…",
  "backoffice.distribution.none.title": "No hay distribución que mostrarte aquí",
  "backoffice.distribution.none.body": "Esta sección es para quien administra una organización o lidera un equipo.",
  "backoffice.distribution.target.label": "Organización",
  "backoffice.distribution.target.organization": "Organización {name}",
  "backoffice.distribution.target.unnamed": "Organización sin nombre",
  "backoffice.distribution.readOnly": "Solo lectura: la distribución la decide quien administra la organización.",
  "backoffice.distribution.warning.exceeds": 
    "Lo distribuido supera el presupuesto disponible o los límites por persona de la organización; ajusta la distribución.",
  "backoffice.distribution.ceiling.title": "Tope de la organización",
  "backoffice.distribution.ceiling.none": "La plataforma no fijó un tope para esta organización.",
  "backoffice.distribution.ceiling.generations": "Generaciones por persona al mes",
  "backoffice.distribution.ceiling.budget": "Presupuesto por persona al mes",
  "backoffice.distribution.ceiling.organization": "Presupuesto de la organización al mes",
  "backoffice.distribution.ceiling.unbounded": "Sin tope",
  "backoffice.distribution.defaults": "Sin límite propio, cada persona tiene {generations} generaciones y {budget} al mes.",
  "backoffice.distribution.people.title": "Límites por persona",
  "backoffice.distribution.people.hint": "Deja un campo vacío para usar el valor por defecto.",
  "backoffice.distribution.people.empty": "Esta organización todavía no tiene personas.",
  "backoffice.distribution.column.person": "Persona",
  "backoffice.distribution.column.generations": "Generaciones al mes",
  "backoffice.distribution.column.budget": "Presupuesto al mes (USD)",
  "backoffice.distribution.column.applies": "Se aplica",
  "backoffice.distribution.column.actions": "Acciones",
  "backoffice.distribution.column.team": "Equipo",
  "backoffice.distribution.column.allocation": "Asignación al mes (USD)",
  "backoffice.distribution.person.formerMember": "Persona que ya no pertenece a la organización",
  "backoffice.distribution.person.generationsLabel": "Generaciones al mes de {name}",
  "backoffice.distribution.person.budgetLabel": "Presupuesto al mes de {name}, en dólares",
  "backoffice.distribution.person.default": "Por defecto",
  "backoffice.distribution.applies": "{generations} generaciones y {budget}",
  "backoffice.distribution.aboveCeiling": "Supera el tope; se aplica el tope.",
  "backoffice.distribution.action.save": "Guardar",
  "backoffice.distribution.action.useDefault": "Usar el valor por defecto",
  "backoffice.distribution.action.removeAllocation": "Quitar la asignación",
  "backoffice.distribution.teams.title": "Asignaciones por equipo",
  "backoffice.distribution.teams.allocated": "Asignado: {allocated} de {budget}.",
  "backoffice.distribution.teams.empty": "Esta organización todavía no tiene equipos.",
  "backoffice.distribution.teams.none": "Sin asignación",
  "backoffice.distribution.team.allocationLabel": "Asignación al mes de {name}, en dólares",
  "backoffice.distribution.team.unknown": "Equipo no disponible",
  "backoffice.distribution.problem.invalidGenerations": "Escribe un número entero de generaciones, o déjalo vacío.",
  "backoffice.distribution.problem.invalidBudget": "Escribe un importe en dólares, por ejemplo 12.50, o déjalo vacío.",
  "backoffice.distribution.problem.generationsAboveCeiling": "Supera el tope de {limit} generaciones por persona.",
  "backoffice.distribution.problem.budgetAboveCeiling": "Supera el tope de {limit} por persona.",
  "backoffice.distribution.problem.invalidAllocation": "Escribe un importe en dólares, por ejemplo 12.50.",
  "backoffice.distribution.problem.requiredAllocation": "Escribe un importe; para quitarla, usa «Quitar la asignación».",
  "backoffice.distribution.problem.overBudget": "Las asignaciones sumarían {total} y superan el presupuesto de la organización ({budget}).",
  "backoffice.distribution.problem.failed": "No se pudo guardar. Inténtalo de nuevo.",
  "backoffice.distribution.problem.thousands": "Escribe el importe sin separador de miles, por ejemplo 1000 o 1.5.",
  "backoffice.distribution.problem.generationsOutOfBounds": "El máximo permitido es {limit} generaciones al mes.",
  "backoffice.distribution.problem.budgetOutOfBounds": "El máximo permitido es {limit} al mes.",
  "backoffice.distribution.preview": "Se guardará {amount}.",
  "backoffice.distribution.stale": "No se pudo actualizar la distribución; se muestra la última que se leyó.",
  "backoffice.nav.audit": "Auditoría",
  "backoffice.nav.people": "Personas",
  "backoffice.nav.teams": "Equipos",
  "backoffice.nav.invitations": "Invitaciones",
  "backoffice.nav.platformOrganizations": "Organizaciones",
  "backoffice.audit.title": "Auditoría",
  "backoffice.audit.intro": "Quién hizo qué y cuándo en tu organización: mockups, versiones, enlaces compartidos, accesos fallidos y cambios de presupuesto.",
  "backoffice.audit.loading": "Cargando la auditoría…",
  "backoffice.audit.none.title": "No hay auditoría que mostrarte aquí",
  "backoffice.audit.none.body": "Esta sección es para quien administra una organización.",
  "backoffice.audit.target.label": "Organización",
  "backoffice.audit.filters.legend": "Filtrar la actividad",
  "backoffice.audit.filters.from": "Desde",
  "backoffice.audit.filters.to": "Hasta",
  "backoffice.audit.filters.rangeHint": "Deja las fechas vacías para ver los últimos 366 días.",
  "backoffice.audit.filters.action": "Acción",
  "backoffice.audit.filters.allActions": "Todas las acciones",
  "backoffice.audit.filters.allRevocations": "Todas las revocaciones de enlaces",
  "backoffice.audit.filters.team": "Equipo",
  "backoffice.audit.filters.allTeams": "Todos los equipos",
  "backoffice.audit.filters.mockup": "Mockup",
  "backoffice.audit.filters.allMockups": "Todos los mockups",
  "backoffice.audit.filters.apply": "Aplicar",
  "backoffice.audit.filters.clear": "Quitar los filtros",
  "backoffice.audit.problem.reversed": "La fecha inicial no puede ser posterior a la final.",
  "backoffice.audit.problem.tooLong": "El rango no puede superar 366 días.",
  "backoffice.audit.list.title": "Actividad registrada",
  "backoffice.audit.empty.title": "No hay actividad registrada",
  "backoffice.audit.empty.body": "Nada coincide con estos filtros en este período.",
  "backoffice.audit.more": "Cargar más",
  "backoffice.audit.column.when": "Fecha y hora",
  "backoffice.audit.column.actor": "Quién",
  "backoffice.audit.column.action": "Acción",
  "backoffice.audit.column.team": "Equipo",
  "backoffice.audit.column.mockup": "Mockup",
  "backoffice.audit.actor.deleted": "Persona eliminada",
  "backoffice.audit.actor.system": "Sistema",
  "backoffice.audit.actor.visitor": "Visitante de un enlace",
  "backoffice.audit.actor.nobody": "Sin persona",
  "backoffice.audit.mockup.none": "Sin mockup",
  "backoffice.audit.mockup.unnamed": "Mockup sin nombre disponible",
  "backoffice.audit.mockup.trashed": "(en la papelera)",
  "backoffice.audit.restarted": "No se pudo seguir desde esa página: la lista se muestra de nuevo desde el principio.",
  "backoffice.audit.action.mockupCreated": "Mockup creado",
  "backoffice.audit.action.mockupScopeChanged": "Mockup cambiado de ámbito",
  "backoffice.audit.action.mockupTrashed": "Mockup enviado a la papelera",
  "backoffice.audit.action.mockupRestored": "Mockup restaurado de la papelera",
  "backoffice.audit.action.mockupDeleted": "Mockup eliminado definitivamente",
  "backoffice.audit.action.versionGenerated": "Versión generada",
  "backoffice.audit.action.shareLinkCreated": "Enlace compartido creado",
  "backoffice.audit.action.shareLinkExpiryChanged": "Vencimiento de un enlace cambiado",
  "backoffice.audit.action.shareLinkRevoked": "Enlace revocado por una persona",
  "backoffice.audit.action.shareLinkRevokedBySystem": "Enlace revocado por el sistema",
  "backoffice.audit.action.sharePasswordFailed": "Contraseña de enlace incorrecta",
  "backoffice.audit.action.loginFailed": "Inicio de sesión fallido",
  "backoffice.audit.action.loginBlocked": "Inicio de sesión bloqueado",
  "backoffice.audit.action.usageCeilingChanged": "Tope de la organización cambiado",
  "backoffice.audit.action.usageDistributionChanged": "Distribución del presupuesto cambiada",
  "backoffice.audit.action.senderLinked": "Número de WhatsApp vinculado",
  "backoffice.audit.action.senderUnlinked": "Número de WhatsApp desvinculado",
  "backoffice.audit.action.designProfileRenamed": "Perfil de diseño renombrado",
  "backoffice.audit.action.unknown": "Otra acción",
  "intake.title": "WhatsApp",
  "intake.intro":
    "Vincula tu número de WhatsApp para crear y modificar mockups enviando mensajes. Elige dónde se crearán, pide un código y envíalo desde tu teléfono.",
  "intake.issue.title": "Vincular un número",
  "intake.issue.scope": "Dónde se crearán tus mockups",
  "intake.issue.submit": "Obtener código",
  "intake.issue.noScopes": "No tienes un equipo ni una organización donde crear mockups, así que todavía no puedes vincular un número.",
  "intake.issue.failed": "No se pudo obtener el código. Inténtalo de nuevo.",
  "intake.code.issued": "Tu código está listo. Envía {command} por WhatsApp antes del {when}. Si pides otro código, este deja de valer.",
  "intake.code.field": "Mensaje para enviar por WhatsApp",
  "intake.code.copy": "Copiar mensaje",
  "intake.code.copied": "Mensaje copiado",
  "intake.code.copyManual": "No pudimos copiar. El mensaje está seleccionado: cópialo tú.",
  "intake.list.title": "Números vinculados",
  "intake.list.empty": "Aún no has vinculado ningún número.",
  "intake.list.refresh": "Actualizar lista",
  "intake.list.linkedOn": "Vinculado el {date}",
  "intake.list.badge": "Vinculado",
  "intake.list.scopePersonal": "Personal",
  "intake.list.scopeUnknown": "Ámbito no disponible",
  "intake.unlink.action": "Desvincular",
  "intake.unlink.for": "Desvincular el número {number}",
  "intake.unlink.confirmTitle": "¿Desvincular este número?",
  "intake.unlink.confirmBody": "Los mensajes que envíes desde ese número dejarán de crear o modificar mockups.",
  "intake.unlink.done": "Número desvinculado.",
  "intake.unlink.failed": "No se pudo desvincular el número.",

  // Mockup v3: one slot per phase, each in its own file, so two phases never edit the same lines.
  ...esAccount,
  ...esOrganization,
  ...esMockupsAccess,
  ...esDesign,
  ...esDesignDelete,
  ...esDesignReadOnly,
  // Mockup v3 wave 2 (P6, P7, P8, P9).
  ...esDiagrams,
  ...esCosts,
  ...esGalleryLinks,
  ...esReports,
} as const;
